import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useSpring, useInView, AnimatePresence } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import { ArrowRight, Send, CheckCircle, Award, Briefcase, Star, Code2, Triangle, Circle, FileCode, Database, Palette, Figma, GitBranch, Crown, Users, MessageSquare, Puzzle, Lightbulb, RefreshCw, Clock, Brain, BarChart, GraduationCap, Sparkles, Rocket, Zap, Target, ChevronLeft, ChevronRight } from 'lucide-react';
import emailjs from '@emailjs/browser';
import BackToTopButton from '../components/BackToTopButton';

const SITE_DATA = {
  hero: {
    firstName: "BEKIRHAN",
    lastName: "SIMSEK",
    role: "FULL-STACK DEVELOPER",
    location: "ARENDONK, BELGIUM",
    description: "I break boundaries to craft designs that stand out and deliver results. Blending creativity with strategy, turning bold ideas into digital experiences.",
    image: "images/proffoto.png",
  },

  projects: [
    { id: 1, title: "BSPARFUMS", category: "E-COMMERCE", image: "https://images.unsplash.com/photo-1588514912908-8f5891714f8d?q=80&w=1169&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D", year: "2024" },
    { id: 2, title: "APPIES LEGOBIB", category: "FULL-STACK", image: "https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=800&h=600&fit=crop", year: "2025" },
    { id: 3, title: "MOTOCONNECT", category: "MOBILE APP", image: "https://images.unsplash.com/photo-1552306062-29a5560e1c31?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D", year: "2024" },
    { id: 4, title: "AMAZON REDESIGN", category: "UX/UI DESIGN", image: "https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=800&h=600&fit=crop", year: "2025" },
    { id: 5, title: "AR RACING", category: "MOBILE APP", image: "https://images.unsplash.com/photo-1724908267988-63310819325c?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D", year: "2025" },
    { id: 6, title: "FRIDGECHEF", category: "FULL-STACK", image: "https://images.unsplash.com/photo-1606859191214-25806e8e2423?q=80&w=1135&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D", year: "2025" },
  ],

  technologies: [
    { name: "React", logo: Code2 },
    { name: "Next.js", logo: Triangle },
    { name: "Node.js", logo: Circle },
    { name: "Laravel", logo: Triangle },
    { name: "Python", logo: Code2 },
    { name: ".NET", logo: FileCode },
    { name: "TypeScript", logo: FileCode },
    { name: "MongoDB", logo: Database },
    { name: "MySQL", logo: Database },
    { name: "Tailwind", logo: Palette },
    { name: "Figma", logo: Figma },
    { name: "Git", logo: GitBranch },
  ],

  services: [
    { number: "01", name: "Web Development", image: "https://images.unsplash.com/photo-1678690833065-965b52c0fa8d?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3Dcrop", description: "Building fast, responsive web applications with React, Next.js, and modern frameworks." },
    { number: "02", name: "UI/UX Design", image: "https://images.unsplash.com/photo-1618788372246-79faff0c3742?q=80&w=627&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3Dcrop", description: "Creating intuitive interfaces that enhance user experience and drive engagement." },
    { number: "03", name: "Backend Development", image: "https://plus.unsplash.com/premium_photo-1664297989345-f4ff2063b212?q=80&w=1098&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3Dcrop", description: "Robust server-side solutions with Node.js, PHP, Laravel, and .NET." },
  ],

  milestones: [
    { number: 3, suffix: "+", label: "Years Experience", icon: "Award" },
    { number: 10, suffix: "+", label: "Projects Completed", icon: "Briefcase" },
    { number: 100, suffix: "%", label: "Testable code", icon: "Test" },
  ],

  skills: [
    { name: "LEADERSHIP", category: "Soft Skill", icon: Crown },
    { name: "TEAMWORK", category: "Soft Skill", icon: Users },
    { name: "COMMUNICATION", category: "Soft Skill", icon: MessageSquare },
    { name: "PROBLEM SOLVING", category: "Competency", icon: Puzzle },
    { name: "CREATIVITY", category: "Competency", icon: Lightbulb },
    { name: "ADAPTABILITY", category: "Soft Skill", icon: RefreshCw },
    { name: "TIME MANAGEMENT", category: "Competency", icon: Clock },
    { name: "CRITICAL THINKING", category: "Competency", icon: Brain },
    { name: "PROJECT MANAGEMENT", category: "Competency", icon: BarChart },
    { name: "MENTORING", category: "Soft Skill", icon: GraduationCap },
  ],

  about: {
    label: "ABOUT US.25",
    title: "ABOUT",
    description: "I craft designs that don't just look stunning, they create impact. Blending creativity with strategy, we transform ideas into immersive digital experiences that captivate, engage, and convert.",
    image: "https://images.unsplash.com/photo-1583129637935-b1af063d2c58?q=80&w=1332&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3DD%3D",
  },

  contact: {
    label: "CONTACT US.25",
    title: "GET IN TOUCH",
    description: "Have a project in mind? Whether you're launching a brand, designing a product, or elevating your digital presence, we're here to bring your vision to life.",
    image: "https://images.unsplash.com/photo-1513346940221-6f673d962e97?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  },

  showcase: {
    label: "CORE VALUES",
    title: "MY CRAFT",
    cards: [
      {
        id: 1,
        title: "CREATIVE",
        subtitle: "DESIGN",
        description: "Crafting unique digital experiences",
        gradient: "from-purple-500 via-pink-500 to-red-500",
        icon: Sparkles,
      },
      {
        id: 2,
        title: "INNOVATIVE",
        subtitle: "SOLUTIONS",
        description: "Pushing boundaries with technology",
        gradient: "from-blue-500 via-cyan-500 to-teal-500",
        icon: Rocket,
      },
      {
        id: 3,
        title: "MODERN",
        subtitle: "APPROACH",
        description: "Using cutting-edge frameworks",
        gradient: "from-orange-500 via-amber-500 to-yellow-500",
        icon: Zap,
      },
      {
        id: 4,
        title: "PIXEL",
        subtitle: "PERFECT",
        description: "Attention to every detail",
        gradient: "from-green-500 via-emerald-500 to-teal-500",
        icon: Target,
      },
    ],
  },
};

function ProgressBar() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  return (
    <motion.div
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 origin-left z-[100]"
    />
  );
}


// SVGs per technology
const TECH_SVGS = {
  "React": (
    <svg viewBox="0 0 32 32" width="32" height="32"><circle cx="16" cy="16" r="2.5" fill="#61DAFB"/><ellipse cx="16" cy="16" rx="14" ry="5" stroke="#61DAFB" strokeWidth="1.5" fill="none"/><ellipse cx="16" cy="16" rx="14" ry="5" stroke="#61DAFB" strokeWidth="1.5" fill="none" transform="rotate(60 16 16)"/><ellipse cx="16" cy="16" rx="14" ry="5" stroke="#61DAFB" strokeWidth="1.5" fill="none" transform="rotate(120 16 16)"/></svg>
  ),
  "Next.js": (
    <svg viewBox="0 0 32 32" width="32" height="32"><circle cx="16" cy="16" r="15" fill="#000"/><path d="M10 22V10l12 15V10" stroke="white" strokeWidth="2.2" fill="none" strokeLinejoin="round" strokeLinecap="round"/></svg>
  ),
  "Node.js": (
    <svg viewBox="0 0 32 32" width="32" height="32"><path d="M16 2L29 9.5v13L16 30 3 22.5v-13L16 2z" fill="#339933"/><text x="9.5" y="21" fill="white" fontSize="10" fontWeight="bold" fontFamily="monospace">JS</text></svg>
  ),
  "Laravel": (
    <svg viewBox="0 0 32 32" width="32" height="32"><path d="M16 2L29 9.5v13L16 30 3 22.5v-13L16 2z" fill="#FF2D20"/><text x="11.5" y="22" fill="white" fontSize="14" fontWeight="bold" fontFamily="sans-serif">L</text></svg>
  ),
  "Python": (
    <svg viewBox="0 0 32 32" width="32" height="32"><path d="M16 2c-5 0-7 3-7 7v4h7v2H7S3 15 3 21s4 7 4 7h3v-4c0-3 2.5-5 6-5h6c4 0 5-2 5-4V9c0-4-2-7-7-7z" fill="#3776AB"/><circle cx="13" cy="8.5" r="1.5" fill="white"/><path d="M16 30c5 0 7-3 7-7v-4h-7v-2h9s4 0 4-6-4-7-4-7h-3v4c0 3-2.5 5-6 5h-6c-4 0-5 2-5 4v7c0 4 2 7 7 7z" fill="#FFD43B"/><circle cx="19" cy="23.5" r="1.5" fill="white"/></svg>
  ),
  ".NET": (
    <svg viewBox="0 0 32 32" width="32" height="32"><rect width="32" height="32" rx="6" fill="#512BD4"/><text x="3" y="22" fill="white" fontSize="10" fontWeight="bold" fontFamily="sans-serif">.NET</text></svg>
  ),
  "TypeScript": (
    <svg viewBox="0 0 32 32" width="32" height="32"><rect width="32" height="32" rx="6" fill="#3178C6"/><text x="4" y="24" fill="white" fontSize="15" fontWeight="bold" fontFamily="sans-serif">TS</text></svg>
  ),
  "MongoDB": (
    <svg viewBox="0 0 32 32" width="32" height="32"><path d="M16 2C16 2 7 11 7 20a9 9 0 0018 0C25 11 16 2 16 2z" fill="#47A248"/><line x1="16" y1="6" x2="16" y2="30" stroke="#AD9123" strokeWidth="2.5" strokeLinecap="round"/></svg>
  ),
  "MySQL": (
    <svg viewBox="0 0 32 32" width="32" height="32"><path d="M6 13c0-4 4.5-6 10-6s10 2 10 6v9c0 4-4.5 6-10 6S6 26 6 22V13z" fill="#00618A"/><ellipse cx="16" cy="13" rx="10" ry="4" fill="#E48E00"/><path d="M6 18c0 3.5 4.5 5.5 10 5.5S26 21.5 26 18" stroke="#005070" strokeWidth="1" fill="none"/></svg>
  ),
  "Tailwind": (
    <svg viewBox="0 0 32 32" width="32" height="32"><path d="M4 13c1.5-6 5.5-9 10-9 9 0 9 8.5 14 9 3 .4 5-1 6-2.5-1.5 6-5.5 9-10 9-9 0-9-8.5-14-9-3-.4-5 1-6 2.5z" fill="#38BDF8"/><path d="M0 21c1.5-6 5.5-9 10-9 9 0 9 8.5 14 9 3 .4 5-1 6-2.5-1.5 6-5.5 9-10 9-9 0-9-8.5-14-9-3-.4-5 1-6 2.5z" fill="#38BDF8" opacity="0.65"/></svg>
  ),
  "Figma": (
    <svg viewBox="0 0 32 32" width="32" height="32"><rect x="4" y="2" width="11" height="11" rx="5.5" fill="#F24E1E"/><rect x="17" y="2" width="11" height="11" rx="5.5" fill="#FF7262"/><rect x="4" y="13" width="11" height="11" rx="3" fill="#A259FF"/><circle cx="22.5" cy="18.5" r="5.5" fill="#1ABCFE"/><rect x="4" y="20" width="11" height="10" rx="5" fill="#0ACF83"/></svg>
  ),
  "Git": (
    <svg viewBox="0 0 32 32" width="32" height="32"><circle cx="9" cy="8" r="3.5" fill="#F05032"/><circle cx="23" cy="8" r="3.5" fill="#F05032"/><circle cx="16" cy="24" r="3.5" fill="#F05032"/><path d="M9 11.5v3.5c0 3 3 5.5 7 5M23 11.5v3c0 2.5-2 4-4 4.5" stroke="#F05032" strokeWidth="2" fill="none" strokeLinecap="round"/><line x1="16" y1="17" x2="16" y2="20.5" stroke="#F05032" strokeWidth="2" strokeLinecap="round"/></svg>
  ),
};

function TechScroller() {
  return (
    <div className="py-12 sm:py-16 lg:py-24 bg-[#161616] overflow-hidden">
      <div className="max-w-[100vw] mx-auto px-4 sm:px-6 lg:px-12 mb-8 sm:mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <span className="text-orange-500 text-xs sm:text-sm tracking-widest mb-3 sm:mb-4 block font-silkscreen">
            TECHNOLOGIES
          </span>
          <h3 className="text-xl sm:text-2xl lg:text-4xl text-white font-silkscreen">
            TOOLS I WORK WITH
          </h3>
        </motion.div>
      </div>

      {/* First Row - Left to Right */}
      <div className="relative mb-6">
        <motion.div
          animate={{ x: [0, -1920] }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="flex gap-6"
        >
          {[...SITE_DATA.technologies, ...SITE_DATA.technologies, ...SITE_DATA.technologies].map((tech, idx) => (
            <div
              key={idx}
              className="flex-shrink-0 bg-[#1a1a1a] hover:bg-[#222] border border-white/5 hover:border-orange-500/30 rounded-2xl px-8 py-5 flex items-center gap-4 transition-all cursor-pointer group"
            >
              <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                {TECH_SVGS[tech.name]}
              </div>
              <span className="text-white/70 group-hover:text-white font-silkscreen whitespace-nowrap transition-colors">
                {tech.name}
              </span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Second Row - Right to Left */}
      <div className="relative">
        <motion.div
          animate={{ x: [-1920, 0] }}
          transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
          className="flex gap-6"
        >
          {[...SITE_DATA.technologies.slice().reverse(), ...SITE_DATA.technologies.slice().reverse(), ...SITE_DATA.technologies.slice().reverse()].map((tech, idx) => (
            <div
              key={idx}
              className="flex-shrink-0 bg-[#1a1a1a] hover:bg-[#222] border border-white/5 hover:border-orange-500/30 rounded-2xl px-8 py-5 flex items-center gap-4 transition-all cursor-pointer group"
            >
              <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                {TECH_SVGS[tech.name]}
              </div>
              <span className="text-white/70 group-hover:text-white font-silkscreen whitespace-nowrap transition-colors">
                {tech.name}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

function AnimatedCounter({ target, suffix = "", duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      // Start counting
      setCount(0);
      let start = 0;
      const increment = target / (duration * 60);

      const timer = setInterval(() => {
        start += increment;
        if (start >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);

      return () => clearInterval(timer);
    } else {
      setCount(0);
    }
  }, [isInView, target, duration]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
}

function ProjectsCarousel() {
  const [startIndex, setStartIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const projects = SITE_DATA.projects;
  const perView = 3;
  const totalPages = Math.ceil(projects.length / perView);
  const currentPage = Math.floor(startIndex / perView);
  const maxStart = projects.length - perView;

  const handlePrev = () => {
    if (startIndex <= 0) return;
    setDirection(-1);
    setStartIndex(i => Math.max(0, i - perView));
  };

  const handleNext = () => {
    if (startIndex >= maxStart) return;
    setDirection(1);
    setStartIndex(i => Math.min(maxStart, i + perView));
  };

  return (
    <div className="relative">
      {/* Navigation row */}
      <div className="flex items-center justify-between mb-6 sm:mb-8">
        <div className="flex gap-2 items-center">
          {Array.from({ length: totalPages }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => { setDirection(idx > currentPage ? 1 : -1); setStartIndex(idx * perView); }}
              style={{ backgroundColor: currentPage === idx ? '#f97316' : '#d1d5db' }}
              className={`h-1.5 rounded-full transition-all duration-300 ${currentPage === idx ? 'w-8' : 'w-3'}`}
            />
          ))}
        </div>
        <div className="flex gap-3">
          <button
            onClick={handlePrev}
            disabled={startIndex === 0}
            className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full border flex items-center justify-center transition-all ${
              startIndex === 0
                ? 'border-white/10 text-white/20 cursor-not-allowed'
                : 'border-white/30 text-white hover:border-orange-500 hover:text-orange-500'
            }`}
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <button
            onClick={handleNext}
            disabled={startIndex >= maxStart}
            className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full border flex items-center justify-center transition-all ${
              startIndex >= maxStart
                ? 'border-white/10 text-white/20 cursor-not-allowed'
                : 'border-white/30 text-white hover:border-orange-500 hover:text-orange-500'
            }`}
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="overflow-hidden">
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div
            key={currentPage}
            custom={direction}
            variants={{
              enter: (d) => ({ opacity: 0, x: d * 60 }),
              center: { opacity: 1, x: 0 },
              exit: (d) => ({ opacity: 0, x: -d * 60 }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6"
          >
            {projects.slice(startIndex, startIndex + perView).map((project) => (
              <div key={project.id} className="group cursor-pointer">
                <Link to={`/projects/${project.id}`}>
                  <div className="relative h-[320px] sm:h-[380px] rounded-[32px] overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/60 group-hover:bg-black/50 transition-colors" />

                    <div className="absolute inset-0 flex flex-col items-center justify-center p-5">
                      <div className="bg-white/10 backdrop-blur-xl px-5 py-5 rounded-2xl border border-white/10 text-center max-w-[260px]">
                        <span className="text-white/60 text-xs tracking-widest block mb-2 font-silkscreen">
                          {project.category}
                        </span>
                        <h3 className="text-base font-bold font-silkscreen text-white">
                          {project.title}
                        </h3>
                      </div>

                      <motion.button className="absolute bottom-5 bg-white text-black px-5 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-4 group-hover:translate-y-0 flex items-center gap-2 font-silkscreen text-xs">
                        VIEW PROJECT
                        <ArrowRight className="w-3.5 h-3.5" />
                      </motion.button>
                    </div>

                    <div className="absolute top-4 right-4 bg-white/10 backdrop-blur-xl px-3 py-1.5 rounded-full border border-white/20">
                      <span className="text-xs font-bold font-silkscreen text-white">{project.year}</span>
                    </div>

                    <div className="absolute inset-0 border-2 border-orange-500 rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function HomePage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      const templateParams = {
        from_name: `${formData.firstName} ${formData.lastName}`,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        message: formData.message || 'Not provided',
        to_name: 'Bekirhan',
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      setLoading(false);
      setSubmitted(true);

      setTimeout(() => {
        setSubmitted(false);
        setFormData({ firstName: '', lastName: '', email: '', phone: '', company: '', message: '' });
      }, 4000);
    } catch (error) {
      console.error('Failed to send email:', error);
      setLoading(false);
      alert('Failed to send message. Please try again or contact me directly at contact@bekirhan.be');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Progress Bar */}
      <ProgressBar />

      <section className="min-h-[80vh] flex items-center justify-center px-4 sm:px-6 lg:px-12 bg-white overflow-hidden py-12 sm:py-20">
        <div className="w-full max-w-[99vw] mx-auto rounded-[40px] sm:rounded-[70px] bg-white p-4 sm:p-6 lg:p-12">
          <div className="grid grid-cols-12 gap-4 sm:gap-6 lg:gap-12 items-center">
            {/* Left - Name */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="col-span-12 lg:col-span-3"
              whileHover={{ x: 10 }}
            >
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.9] tracking-tight font-silkscreen text-black">
                <TypeAnimation
                  sequence={['BEKIRHAN', 3000, 'DEVELOPER', 2000]}
                  wrapper="span"
                  speed={50}
                  repeat={Infinity}
                />
              </h1>
              <p className="text-gray-500 text-[10px] sm:text-xs tracking-widest mt-3 sm:mt-4 font-silkscreen">
                {SITE_DATA.hero.role}
              </p>
            </motion.div>

            {/* Center - Photo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="col-span-12 lg:col-span-6 flex justify-center my-6 sm:my-8 lg:my-0"
              whileHover={{ scale: 1.02, transition: { type: "spring", stiffness: 300 } }}
            >
              <div className="relative">
                {/* Rotating Border */}
                <motion.div
                  className="absolute -inset-3 sm:-inset-4 border border-black/10 rounded-[30px] sm:rounded-[50px]"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                />

                <img
                  src={SITE_DATA.hero.image}
                  alt="Bekirhan Simsek"
                  className="w-full max-w-[220px] sm:max-w-[280px] md:max-w-[350px] lg:max-w-[400px] xl:max-w-[450px] aspect-[3/4] object-cover rounded-[30px] sm:rounded-[40px] shadow-2xl grayscale hover:grayscale-0 transition-all duration-700 cursor-pointer"
                />

                {/* Available Badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1, type: "spring" }}
                  className="absolute -bottom-3 sm:-bottom-4 left-1/2 -translate-x-1/2 bg-orange-500 text-white px-3 sm:px-5 py-1.5 sm:py-2 rounded-full text-[10px] sm:text-xs whitespace-nowrap shadow-lg font-silkscreen"
                >
                  AVAILABLE FOR WORK
                </motion.div>
              </div>
            </motion.div>

            {/* Right - Second Name & Description */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="col-span-12 lg:col-span-3 text-center lg:text-right"
              whileHover={{ x: -10 }}
            >
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-[0.9] tracking-tight font-silkscreen text-black">
                <TypeAnimation
                  sequence={['', 1000, 'SIMSEK', 3000, 'CREATOR', 2000]}
                  wrapper="span"
                  speed={50}
                  repeat={Infinity}
                />
              </h1>
              <p className="text-gray-500 text-[10px] sm:text-xs tracking-widest mt-3 sm:mt-4 mb-4 sm:mb-6 font-silkscreen">
                {SITE_DATA.hero.location}
              </p>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed max-w-[260px] sm:max-w-[280px] mx-auto lg:ml-auto lg:mr-0">
                {SITE_DATA.hero.description}
              </p>
            </motion.div>
          </div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="flex justify-center mt-8 sm:mt-12 lg:mt-16"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="flex flex-col items-center gap-2 text-black/30"
            >
              <span className="text-[10px] sm:text-xs tracking-widest font-silkscreen">SCROLL</span>
              <div className="w-px h-6 sm:h-8 bg-gradient-to-b from-black/30 to-transparent" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-8 sm:py-12 px-4 sm:px-6 lg:px-12 bg-gradient-to-b from-white to-black">
        <span className="text-orange-500 text-xs tracking-widest mb-3 sm:mb-4 block font-silkscreen text-center">
          SELECTED WORKS
        </span>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-silkscreen text-black text-center mb-8 sm:mb-12">
          PROJECTS
        </h2>
        <div className="max-w-[99vw] mx-auto">
          <ProjectsCarousel />
          <div className="flex justify-center mt-10">
            <Link to="/projects">
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className="group flex items-center gap-3 bg-black text-white border border-white/20 hover:border-orange-500 hover:bg-orange-500 px-8 py-3.5 rounded-full font-silkscreen text-sm transition-all duration-300"
              >
                SEE ALL PROJECTS
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </Link>
          </div>
        </div>
      </section>

      <div className="py-5 px-6 lg:px-12 bg-black">
        <div className="rounded-[70px] overflow-hidden">
          <TechScroller />
        </div>
      </div>

      <section className="py-5 px-4 sm:px-6 lg:px-12 bg-black overflow-hidden">
        <div className="max-w-[99vw] mx-auto rounded-[40px] sm:rounded-[70px] bg-[#161616] p-4 sm:p-8 lg:p-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-12 lg:mb-16"
          >
            <span className="text-orange-500 text-xs sm:text-sm tracking-widest mb-3 sm:mb-4 block font-silkscreen">
              ACHIEVEMENTS
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-silkscreen text-white">
              MILESTONES
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {SITE_DATA.milestones.map((milestone, idx) => {
              const IconComponent = milestone.icon === 'Award' ? Award : milestone.icon === 'Briefcase' ? Briefcase : Star;

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="group relative"
                >
                  {/* Subtle glow on hover */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500/20 to-amber-500/20 rounded-[32px] opacity-0 group-hover:opacity-100 blur-lg transition-opacity duration-500" />

                  {/* Card */}
                  <div className="relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/5 group-hover:border-orange-500/30 rounded-[28px] sm:rounded-[32px] p-5 sm:p-6 lg:p-8 transition-all duration-300 overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-[0.02]"
                      style={{
                        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                        backgroundSize: '24px 24px',
                      }}
                    />

                    {/* Icon - Top Left */}
                    <div className="relative mb-4 sm:mb-5">
                      <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/5 border border-white/10 group-hover:border-orange-500/50 group-hover:bg-orange-500/10 transition-all duration-300">
                        <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-white/40 group-hover:text-orange-500 transition-colors duration-300" strokeWidth={1.5} />
                      </div>
                    </div>

                    {/* Counter */}
                    <div className="relative mb-2 sm:mb-3">
                      <div className="text-4xl sm:text-5xl font-silkscreen text-white group-hover:text-orange-500 transition-colors duration-300">
                        <AnimatedCounter target={milestone.number} suffix={milestone.suffix} duration={2} />
                      </div>
                    </div>

                    {/* Label */}
                    <div className="relative">
                      <p className="text-white/50 group-hover:text-white/70 text-xs sm:text-sm font-silkscreen tracking-wide transition-colors duration-300 uppercase">
                        {milestone.label}
                      </p>
                    </div>

                    {/* Decorative line */}
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>


      <section className="py-5 px-4 sm:px-6 lg:px-12 bg-black overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative max-w-[99vw] mx-auto rounded-[40px] sm:rounded-[70px] overflow-hidden min-h-[400px] sm:min-h-[500px] lg:min-h-[600px]"
        >
          {/* Background Image */}
          <img
            src={SITE_DATA.about.image}
            alt="About"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Gradient Overlay with Sand Effect */}
          <div className="absolute inset-0 bg-gradient-to-l from-black/80 via-black/60 to-transparent" />
          <div className="absolute inset-0 opacity-30 mix-blend-overlay"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
            }}
          />

          {/* Content */}
          <div className="relative z-10 p-4 sm:p-8 lg:p-16 xl:p-20 h-full flex items-center justify-between gap-4 sm:gap-6 min-h-[400px] sm:min-h-[500px] lg:min-h-[600px]">
            {/* Glassmorphic Card - Responsive Width */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="w-full lg:w-[80%] bg-white/10 backdrop-blur-xl rounded-[20px] sm:rounded-[32px] p-4 sm:p-8 lg:p-10 border border-white/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-8"
            >
              {/* Left Side - Label & Title */}
              <div className="flex-shrink-0 min-w-0">
                <span className="text-white/60 text-xs sm:text-sm tracking-widest mb-2 sm:mb-4 block font-silkscreen">
                  {SITE_DATA.about.label}
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-silkscreen text-white break-words">
                  {SITE_DATA.about.title}
                </h2>
              </div>

              {/* Right Side - Button */}
              <div className="flex-shrink-0">
                <Link to="/about">
                  <button className="group flex items-center gap-2 sm:gap-3 bg-orange-500 hover:bg-orange-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-all font-silkscreen text-xs sm:text-sm whitespace-nowrap">
                    LEARN MORE
                    <ArrowRight className="w-3 sm:w-4 h-3 sm:h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
              </div>
            </motion.div>

            {/* Description - 20% Width (Outside Glassmorphism, Right Side) */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden lg:block w-[20%] pl-6"
            >
              <p className="text-white/70 text-sm xl:text-base font-bold leading-relaxed">
                {SITE_DATA.about.description}
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      <section className="py-5 px-4 sm:px-6 lg:px-12 bg-black overflow-hidden">
        <div className="max-w-[99vw] mx-auto rounded-[40px] sm:rounded-[70px] bg-[#161616] p-4 sm:p-8 lg:p-16">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-4 sm:gap-6 mb-8 sm:mb-12 lg:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-orange-500 text-xs sm:text-sm tracking-widest mb-3 sm:mb-4 block font-silkscreen">
                EXPERTISE
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-silkscreen text-white break-words">
                SKILLS
              </h2>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-white/50 max-w-md text-sm sm:text-base lg:text-right"
            >
              Core competencies and soft skills that drive my professional growth and enable effective collaboration.
            </motion.p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {SITE_DATA.skills.map((skill, idx) => {
              const IconComponent = skill.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  whileHover={{ scale: 1.05, backgroundColor: '#1a1a1a' }}
                  className="bg-[#151515] rounded-[24px] p-6 border border-white/5 hover:border-orange-500/50 transition-all cursor-pointer text-center group"
                >
                  <div className="mb-3 group-hover:scale-110 transition-transform flex items-center justify-center">
                    <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" />
                  </div>
                  <span className="text-xs sm:text-sm text-white/80 block font-silkscreen">
                    {skill.name}
                  </span>
                  <span className="text-white/30 text-[10px] tracking-widest mt-1.5 block font-silkscreen">
                    {skill.category}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-12 bg-black overflow-hidden">
        <div className="max-w-[1400px] mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8 sm:mb-12 lg:mb-16 text-center"
          >
            <span className="text-orange-500 text-xs sm:text-sm tracking-widest mb-3 sm:mb-4 block font-silkscreen">
              {SITE_DATA.showcase.label}
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-silkscreen text-white break-words">
              {SITE_DATA.showcase.title}
            </h2>
          </motion.div>

          {/* 4 Cards in Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {SITE_DATA.showcase.cards.map((card, index) => {
              const IconComponent = card.icon;
              return (
                <motion.div
                  key={card.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="group relative"
                >
                  {/* Glow effect on hover */}
                  <div className={`absolute -inset-1 rounded-[28px] opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500 bg-gradient-to-br ${card.gradient}`} />

                  {/* Card */}
                  <div className="relative bg-[#0a0a0a] border border-white/10 group-hover:border-white/20 rounded-[28px] p-8 transition-all duration-300 h-full min-h-[280px] flex flex-col">
                    {/* Icon */}
                    <div className="mb-6 flex items-center justify-center">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${card.gradient} flex items-center justify-center shadow-lg`}>
                        <IconComponent className="w-7 h-7 text-white" strokeWidth={1.5} />
                      </div>
                    </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold mb-2 font-silkscreen">
                    <span className={`bg-gradient-to-r ${card.gradient} bg-clip-text text-transparent`}>
                      {card.title}
                    </span>
                  </h3>

                  {/* Subtitle */}
                  <h4 className="text-xl font-bold mb-4 font-silkscreen text-white/30">
                    {card.subtitle}
                  </h4>

                  {/* Description */}
                  <p className="text-white/50 leading-relaxed mt-auto">
                    {card.description}
                  </p>
                </div>
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>

    

      <section className="py-5 px-6 lg:px-12 bg-black">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative max-w-[99vw] mx-auto rounded-[70px] overflow-hidden"
        >
          {/* Background Image */}
          <img
            src={SITE_DATA.contact.image}
            alt="Contact"
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/90" />

          {/* Content */}
          <div className="relative z-10 p-8 lg:p-16 xl:p-20">
            {/* Title Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-orange-500 text-sm tracking-widest mb-4 block font-silkscreen">
                {SITE_DATA.contact.label}
              </span>
              <h2 className="text-3xl lg:text-5xl mb-6 font-silkscreen text-white">
                {SITE_DATA.contact.title}
              </h2>
              <p className="text-white/70 text-lg max-w-2xl mx-auto">
                {SITE_DATA.contact.description}
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
            {/* Left - Business Card Preview */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:sticky lg:top-24"
            >
              <div className="relative perspective-1000">
                {/* Business Card */}
                <motion.div
                  animate={{
                    rotateY: [0, 5, 0, -5, 0],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="relative w-full max-w-md mx-auto aspect-[1.6/1] rounded-2xl sm:rounded-[32px] overflow-hidden shadow-2xl transform-gpu"
                  style={{
                    transformStyle: "preserve-3d",
                  }}
                >
                  {/* Card Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-black via-slate-800 to-#FFCA28" />

                  {/* Animated Gradient Overlay */}
                  <motion.div
                    animate={{
                      backgroundPosition: ['0% 0%', '100% 100%'],
                    }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: 'reverse' }}
                    className="absolute inset-0 opacity-30"
                    style={{
                      backgroundImage: 'linear-gradient(45deg, transparent, rgba(249, 115, 22, 0.3), transparent)',
                      backgroundSize: '200% 200%',
                    }}
                  />

                  {/* Card Content */}
                  <div className="relative z-10 p-4 sm:p-8 h-full flex flex-col justify-between">
                    {/* Top Section */}
                    <div>
                      <div className="flex justify-between items-start mb-3 sm:mb-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg sm:rounded-xl 
                        flex items-center justify-center text-white text-lg sm:text-xl font-permanent-marker shadow-lg">
                          BS
                        </div>
                        <div className="text-right">
                          <div className="text-orange-500 text-[10px] sm:text-xs font-silkscreen">BUSINESS CARD</div>
                        </div>
                      </div>
                    </div>

                    {/* Middle Section - Name */}
                    <div>
                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold font-silkscreen text-white mb-1">
                        {formData.firstName || "YOUR"} {formData.lastName || "NAME"}
                      </h3>
                      <p className="text-orange-500 text-xs sm:text-sm font-silkscreen">
                        {formData.company || "COMPANY / ROLE"}
                      </p>
                    </div>

                    {/* Bottom Section - Contact Info */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-white/80 text-xs sm:text-sm">
                        <div className="w-1 h-1 bg-orange-500 rounded-full" />
                        <span className="truncate">{formData.email || "email@example.com"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/80 text-xs sm:text-sm">
                        <div className="w-1 h-1 bg-orange-500 rounded-full" />
                        <span>{formData.phone || "+XX XXX XXX XXX"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Decorative Pattern */}
                  <div className="absolute bottom-0 right-0 w-32 h-32 opacity-10">
                    <div className="absolute inset-0" style={{
                      backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                      backgroundSize: '20px 20px',
                    }} />
                  </div>
                </motion.div>

                {/* Shadow/Glow */}
                <div className="absolute inset-0 bg-orange-500/20 blur-3xl -z-10 scale-95" />
              </div>

              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="text-center text-white/50 text-xs sm:text-sm mt-4 sm:mt-6 font-silkscreen px-4"
              >
                Fill out the form to send your digital business card
              </motion.p>
            </motion.div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] rounded-2xl sm:rounded-[32px] p-6 sm:p-8 lg:p-10 border border-white/10"
            >
              {submitted ? (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-3xl font-bold mb-4 font-silkscreen text-white">
                    CARD SENT!
                  </h3>
                  <p className="text-white/60">
                    Thank you! Your digital business card has been sent. I'll get back to you soon.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/40 text-xs tracking-widest mb-2 block font-silkscreen">
                        FIRST NAME *
                      </label>
                      <input
                        type="text"
                        placeholder="John"
                        required
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="w-full bg-black/40 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-orange-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-white/40 text-xs tracking-widest mb-2 block font-silkscreen">
                        LAST NAME *
                      </label>
                      <input
                        type="text"
                        placeholder="Doe"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="w-full bg-black/40 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-orange-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-white/40 text-xs tracking-widest mb-2 block font-silkscreen">
                      EMAIL *
                    </label>
                    <input
                      type="email"
                      placeholder="john@example.com"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-black/40 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="text-white/40 text-xs tracking-widest mb-2 block font-silkscreen">
                      PHONE
                    </label>
                    <input
                      type="tel"
                      placeholder="+32 123 456 789"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-black/40 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="text-white/40 text-xs tracking-widest mb-2 block font-silkscreen">
                      COMPANY / ROLE
                    </label>
                    <input
                      type="text"
                      placeholder="Your Company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full bg-black/40 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="text-white/40 text-xs tracking-widest mb-2 block font-silkscreen">
                      MESSAGE
                    </label>
                    <textarea
                      placeholder="Tell me about your project..."
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-black/40 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-orange-500 transition-colors resize-none"
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-orange-500/50 disabled:to-orange-600/50 text-white rounded-2xl px-6 py-4 font-bold flex items-center justify-center gap-2 transition-all font-silkscreen shadow-lg shadow-orange-500/20"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        SEND CARD
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </motion.button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
        </motion.div>
      </section>

      
      <BackToTopButton />
    </motion.div>
  );
}

export default HomePage;